import React, { useEffect } from 'react';
import { JSONSchema7 } from 'json-schema';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import clsx from 'clsx';
import {
  getSchemaDisplayName,
  detectAnyOfSelectedIndex,
  buildMergedAnyOfValue,
  isJsonSchema,
  toJsonSchema,
  isObjectRecord,
} from 'src/modules/shared/utils/json-schema.util';
import { FormFieldProps } from 'src/modules/shared/types/json-schema.type';
import { SubSchemaFields } from 'src/modules/shared/components/molecules/json-schema-form/any-of-field/sub-schema-fields';

/**
 * Props for AnyOfField component
 */
export interface AnyOfFieldProps extends FormFieldProps {
  value: unknown;
  anyOfSelections: Record<string, number>;
  setAnyOfSelections: (selections: Record<string, number>) => void;
}

export const AnyOfField: React.FC<AnyOfFieldProps> = ({
  fieldKey,
  schema,
  value,
  handleChange,
  anyOfSelections,
  setAnyOfSelections,
  disabled = false,
  allFieldsOptional = false,
  JSONSchemaForm,
}) => {
  const anyOf = schema.anyOf;
  const isOptional = allFieldsOptional;
  const isEmptyValue =
    isOptional &&
    (value === undefined ||
      value === null ||
      (isObjectRecord(value) && Object.keys(value).length === 0));

  // Keep internal selection state in sync with current value/type
  useEffect(() => {
    if (!Array.isArray(anyOf) || anyOf.length === 0) {
      return;
    }

    const currentExplicit = anyOfSelections[fieldKey];

    // When the field is optional and has no value, use -1 to represent
    // "no fixed configuration" instead of defaulting to the first anyOf.
    const hasValidExplicitSelection =
      typeof currentExplicit === 'number' && currentExplicit >= 0;
    const explicitSelectionOrUndefined = hasValidExplicitSelection
      ? currentExplicit
      : undefined;

    const desiredIndex = isEmptyValue
      ? -1
      : detectAnyOfSelectedIndex(anyOf, value, explicitSelectionOrUndefined);

    if (currentExplicit === undefined || currentExplicit !== desiredIndex) {
      setAnyOfSelections({
        ...anyOfSelections,
        [fieldKey]: desiredIndex,
      });
    }
  }, [
    anyOf,
    anyOfSelections,
    fieldKey,
    isEmptyValue,
    setAnyOfSelections,
    value,
  ]);

  const hasNoValidAnyOfSchemas = !Array.isArray(anyOf) || anyOf.length === 0;
  if (hasNoValidAnyOfSchemas) {
    return null;
  }

  const explicitIndex = anyOfSelections[fieldKey];
  const isNoneSelected =
    allFieldsOptional &&
    (explicitIndex === -1 || (explicitIndex === undefined && isEmptyValue));

  const selectedIndex = detectAnyOfSelectedIndex(
    anyOf,
    value,
    isNoneSelected ? undefined : explicitIndex
  );
  const selectedRaw = anyOf[selectedIndex];
  const selectedSchema: JSONSchema7 = toJsonSchema(selectedRaw);

  const handleNoneSelection = () => {
    setAnyOfSelections({
      ...anyOfSelections,
      [fieldKey]: -1,
    });

    // Unset the field value so it is treated as not configured
    handleChange(fieldKey, undefined);
  };

  const handleAnyOfSelection = (index: number) => {
    setAnyOfSelections({
      ...anyOfSelections,
      [fieldKey]: index,
    });

    const maybeSelected = anyOf?.[index];
    const selected = isJsonSchema(maybeSelected) ? maybeSelected : undefined;
    const mergedValue = buildMergedAnyOfValue(selected, value);
    handleChange(fieldKey, mergedValue);
  };

  const getSubSchemaBorderColor = (index: number): string => {
    if (index === 0) return 'border-blue-400';
    if (index === 1) return 'border-green-400';
    if (index === 2) return 'border-orange-400';

    return 'border-gray-300';
  };

  const subSchemaContainerClassName = clsx(
    'border-l-2 pl-3',
    getSubSchemaBorderColor(selectedIndex)
  );

  const noneOptionLabel =
    fieldKey === 'auth'
      ? // TODO: check when implementing i18n
        'Sin autenticación'
      : // TODO: check when implementing i18n
        'Sin valor';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {allFieldsOptional && (
          <Button
            key="none"
            variant="secondary"
            appearance={isNoneSelected ? 'filled' : 'outline'}
            size="xs"
            onClick={(e) => {
              e.preventDefault();
              handleNoneSelection();
            }}
            type="button"
            className="min-w-[80px]"
          >
            {noneOptionLabel}
          </Button>
        )}
        {anyOf.map((entry, index) => {
          const label = isJsonSchema(entry)
            ? getSchemaDisplayName(entry, index)
            : // TODO: check when implementing i18n
              `Opción ${index + 1}`;

          return (
            <Button
              key={index}
              variant="secondary"
              appearance={
                !isNoneSelected && selectedIndex === index
                  ? 'filled'
                  : 'outline'
              }
              size="xs"
              onClick={(e) => {
                e.preventDefault();
                handleAnyOfSelection(index);
              }}
              type="button"
              className="min-w-[80px]"
            >
              {label}
            </Button>
          );
        })}
      </div>

      {!isNoneSelected && (
        <div className={subSchemaContainerClassName}>
          <SubSchemaFields
            schemaKey={fieldKey}
            schema={selectedSchema}
            value={value}
            handleChange={handleChange}
            disabled={disabled}
            JSONSchemaForm={JSONSchemaForm}
          />
        </div>
      )}
    </div>
  );
};
